/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/_chrome/rail-marker-nav.tsx`.
 *
 * Changes: next/link → @excellent/ui/router (RouterLink). Copied into
 * client/shell/chrome/ — never import from app/ (PRD D6).
 */

import { RouterLink as Link } from "@excellent/ui/router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@excellent/ui";

/**
 * RailMarkerNav — the nested rail sub-menu grammar, extracted as a reusable
 * primitive: a vertical list with a faint guide hairline down its left and a
 * single 14px active marker that SLIDES between rows as the active item changes.
 * This is the exact treatment the left ModeRail uses for a mode's nested views
 * (see `mode-rail.tsx` `ModeSubNav`); the right gutter rail (`ContextRail`)
 * renders its doc list + on-this-page TOC with it so both sides share one design.
 *
 * The line spans only the range the marker can travel (first row centre → last
 * row centre), so selecting the top/bottom item leaves it flush with no overhang.
 * Pure presentation driven by `items[].active`; the caller owns active logic
 * (pathname match for links, scroll-spy for a TOC).
 */

export interface RailMarkerNavItem {
  /** Stable React key + identity for the active marker. */
  key: string;
  label: string;
  /** Pre-rendered icon JSX (e.g. a doc glyph). Sized to 14px here. */
  icon?: ReactNode;
  /** Navigation link target. Rendered as a RouterLink unless `onClick` is set. */
  href?: string;
  /** Click handler (e.g. smooth-scroll a TOC anchor); rendered as an <a>. */
  onClick?: () => void;
  active?: boolean;
  /** Dimmed (e.g. a not-yet-generated doc). */
  dim?: boolean;
  /** Trailing badge text (e.g. "new"). */
  badge?: string;
  /** Indent a level (e.g. a TOC H3 under its H2). */
  indent?: boolean;
}

/** Fixed icon-height (14px) marker; the guide line is sized to its travel range. */
const MARKER_H = 14;
const MARKER_HALF = MARKER_H / 2;

export function RailMarkerNav({
  items,
  showLine = true,
}: {
  items: RailMarkerNavItem[];
  /**
   * Render the guide hairline + sliding active marker (the "this is a navigation
   * track" treatment). Turn OFF for a plain list of links that aren't in-page
   * navigation points — e.g. a list of documents — which still highlights the
   * active row but carries no track. Defaults on.
   */
  showLine?: boolean;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const [markerTop, setMarkerTop] = useState<number | null>(null);
  const [line, setLine] = useState<{ top: number; height: number } | null>(null);
  const activeKey = items.find((i) => i.active)?.key ?? null;

  // Measure the active row's centre + the line's travel range, and re-measure
  // when the active item changes (slides the marker) or the rail's metrics shift
  // (icons mounting late, fonts settling, the rail resizing). Skipped when the
  // track is off.
  useEffect(() => {
    if (!showLine) {
      setLine(null);
      setMarkerTop(null);
      return;
    }
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const listTop = list.getBoundingClientRect().top;
      const centreOf = (el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        return r.top - listTop + r.height / 2;
      };
      const rows = Array.from(list.querySelectorAll<HTMLElement>(":scope > li"));
      if (rows.length === 0) {
        setLine(null);
        setMarkerTop(null);
        return;
      }
      const first = centreOf(rows[0]!);
      const last = centreOf(rows[rows.length - 1]!);
      setLine({ top: first - MARKER_HALF, height: last - first + MARKER_H });
      const activeEl = list.querySelector<HTMLElement>('[data-active="true"]');
      setMarkerTop(activeEl ? centreOf(activeEl) : null);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [activeKey, items, showLine]);

  return (
    <ul
      ref={listRef}
      className={cn("relative flex flex-col gap-px", showLine && "ml-3")}
    >
      {showLine && line ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 w-px bg-foreground/10 transition-[top,height] duration-300 ease-out motion-reduce:transition-none"
          style={{ top: line.top, height: line.height }}
        />
      ) : null}
      {showLine && markerTop !== null ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 h-3.5 w-0.5 -translate-y-1/2 rounded-full bg-foreground transition-[top] duration-300 ease-out motion-reduce:transition-none"
          style={{ top: markerTop }}
        />
      ) : null}
      {items.map((item) => {
        const inner = (
          <>
            {/* No icons in the nav — EVER (Matt, 2026-07-23). Label + badge only. */}
            <span className="min-w-0 flex-1 truncate" title={item.label}>
              {item.label}
            </span>
            {item.badge ? (
              <span className="shrink-0 type-subtext uppercase tracking-[0.08em] text-faint">
                {item.badge}
              </span>
            ) : null}
          </>
        );
        const cls = cn(
          "flex items-center gap-2 py-1 pr-2.5 type-subtext tracking-tight outline-none transition-colors",
          item.indent ? "pl-7" : "pl-3",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          item.active
            ? "font-medium text-foreground/90"
            : item.dim
              ? "text-faint/70 hover:text-subtle"
              : "text-faint hover:text-foreground",
        );
        return (
          <li key={item.key}>
            {item.onClick ? (
              <a
                href={item.href ?? "#"}
                data-active={item.active ? "true" : undefined}
                aria-current={item.active ? "true" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  item.onClick?.();
                }}
                className={cls}
              >
                {inner}
              </a>
            ) : item.href ? (
              <Link
                href={item.href}
                data-active={item.active ? "true" : undefined}
                aria-current={item.active ? "page" : undefined}
                className={cls}
              >
                {inner}
              </Link>
            ) : (
              <span data-active={item.active ? "true" : undefined} className={cls}>
                {inner}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
